import { ArrowUpRight, Check, Code2, Copy, ExternalLink, Globe, Link2, X } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SiShopify, SiWordpress } from 'react-icons/si';
import { ReadDocumentationButton } from '@/Domain/components/Integration/components/ReadDocumentationButton';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { WidgetScript } from '@/Domain/components/WidgetScript';
import { useCopiedFeedback } from '@/Domain/hooks/App/useCopiedFeedback.hook';
import { CopyField } from '@/UI/Website/Install/CopyField';
import { EmbedIllustration, WidgetIllustration } from '@/UI/Website/Install/InstallIllustrations';
import {
  DOCS_SHOPIFY_URL,
  DOCS_WEBSITE_URL,
  DOCS_WORDPRESS_URL,
  WORDPRESS_PLUGIN_URL,
  createEmbedSnippet,
  createShopifyWidgetSnippet,
  createWidgetSnippet,
} from '@/UI/Website/Install/installSnippets';

export type InstallMethodKey = 'manual' | 'wordpress' | 'shopify' | 'others' | 'chat_page';

const SHOPIFY_ADMIN_URL = 'https://admin.shopify.com/';
// `link-underline` opts back into an underline: the global `a` reset strips
// text-decoration, so a plain Tailwind `underline` would be overridden here.
const SHOPIFY_INSTRUCTION_LINK =
  'font-semibold text-foreground link-underline underline-offset-2 transition-colors hover:text-muted-foreground';

export interface InstallMethodMeta {
  key: InstallMethodKey;
  Icon: ComponentType<{ className?: string }>;
  // Brand hex for WordPress/Shopify; a distinct accent hue for the generic methods.
  color: string;
}

export const INSTALL_METHODS: InstallMethodMeta[] = [
  { key: 'manual', Icon: Code2, color: '#6366F1' },
  { key: 'wordpress', Icon: SiWordpress, color: '#21759B' },
  { key: 'shopify', Icon: SiShopify, color: '#95BF47' },
  { key: 'others', Icon: Globe, color: '#0891B2' },
  { key: 'chat_page', Icon: Link2, color: '#7C3AED' },
];

const Step = ({ index, children }: { index: number; children: ReactNode }) => (
  <li className="flex gap-3">
    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-foreground">
      {index}
    </span>
    <div className="min-w-0 flex-1 space-y-2 text-sm text-foreground">{children}</div>
  </li>
);

const SnippetSection = ({
  illustration,
  label,
  description,
  script,
  onCopy,
}: {
  illustration?: ReactNode;
  label: string;
  description: string;
  script: string;
  onCopy: () => void;
}) => (
  <div className="min-w-0 space-y-1.5">
    {illustration}
    <Label>{label}</Label>
    <p className="text-sm text-muted-foreground">{description}</p>
    <WidgetScript script={script} onCopy={onCopy} />
  </div>
);

const ShareLinkRow = ({
  title,
  description,
  url,
  openLabel,
  copyLabel,
  copiedLabel,
  testidPrefix,
}: {
  title: string;
  description: string;
  url: string | undefined;
  openLabel: string;
  copyLabel: string;
  copiedLabel: string;
  testidPrefix: string;
}) => {
  const { copied, markCopied } = useCopiedFeedback();
  const handleCopy = () => {
    if (!url || copied) return;
    navigator.clipboard
      .writeText(url)
      .then(markCopied, (err) => console.error('Could not copy link: ', err));
  };
  return (
    <div className="space-y-2" data-testid={testidPrefix}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={!url}
          className="flex-1 justify-center"
          onClick={() => url && window.open(url, '_blank', 'noopener,noreferrer')}
          data-testid={`${testidPrefix}-open`}
        >
          <ExternalLink className="size-4" />
          {openLabel}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!url || copied}
          className="flex-1 justify-center"
          onClick={handleCopy}
          data-testid={`${testidPrefix}-copy`}
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          {copied ? copiedLabel : copyLabel}
        </Button>
      </div>
    </div>
  );
};

interface InstallMethodSheetProps {
  method: InstallMethodKey | null;
  onOpenChange: (open: boolean) => void;
  scenarioId: string;
  hasShopifyMcp: boolean;
  chatPageUrl: string | undefined;
  widgetPreviewUrl: string | undefined;
  embedPreviewUrl: string | undefined;
  shopifyEditorUrl: string | null | undefined;
  onAgentIdCopy: () => void;
  onSnippetCopy: (snippetType: 'widget' | 'embed' | 'widget_shopify') => void;
}

export const InstallMethodSheet = ({
  method,
  onOpenChange,
  scenarioId,
  hasShopifyMcp,
  chatPageUrl,
  widgetPreviewUrl,
  embedPreviewUrl,
  shopifyEditorUrl,
  onAgentIdCopy,
  onSnippetCopy,
}: InstallMethodSheetProps) => {
  const { t } = useTranslation(['website', 'widget', 'common']);

  const widgetSnippet = createWidgetSnippet(scenarioId);
  const embedSnippet = createEmbedSnippet(scenarioId);
  const shopifySnippet = hasShopifyMcp
    ? createShopifyWidgetSnippet(scenarioId)
    : createWidgetSnippet(scenarioId);

  // Only stores connected via our Shopify app (OAuth) get an editor deep-link; they enable the app embed, others paste the script manually.
  const isShopifyAppUser = !!shopifyEditorUrl;
  const shopifyVariant = isShopifyAppUser ? 'app_embed' : 'manual';

  const shopifyInstructionComponents = {
    admin_link: (
      <a
        href={SHOPIFY_ADMIN_URL}
        target="_blank"
        rel="noreferrer"
        className={SHOPIFY_INSTRUCTION_LINK}
        data-testid="install-sheet-shopify-admin-link"
      />
    ),
    editor_link: (
      <a
        href={shopifyEditorUrl ?? undefined}
        target="_blank"
        rel="noreferrer"
        className={SHOPIFY_INSTRUCTION_LINK}
        data-testid="install-sheet-shopify-editor-link"
      />
    ),
    strong: <strong className="font-semibold text-foreground" />,
  };

  const renderBody = () => {
    switch (method) {
      case 'manual':
        return (
          <div className="space-y-6">
            <SnippetSection
              illustration={<WidgetIllustration />}
              label={t('widget:web_widget.label')}
              description={t('widget:web_widget.description')}
              script={widgetSnippet}
              onCopy={() => onSnippetCopy('widget')}
            />
            <SnippetSection
              illustration={<EmbedIllustration />}
              label={t('widget:embed_integration.label')}
              description={t('widget:embed_integration.description')}
              script={embedSnippet}
              onCopy={() => onSnippetCopy('embed')}
            />
          </div>
        );
      case 'wordpress':
        return (
          <div className="space-y-5">
            <Button
              variant="secondary"
              size="md"
              className="w-full"
              onClick={() => window.open(WORDPRESS_PLUGIN_URL, '_blank', 'noopener,noreferrer')}
              data-testid="install-sheet-wordpress-plugin"
            >
              {t('website:install.sheet.open_plugin')}
              <ArrowUpRight className="size-4" />
            </Button>
            <ol className="space-y-4">
              <Step index={1}>{t('website:install.sheet.wordpress.step1')}</Step>
              <Step index={2}>
                <p>{t('website:install.sheet.wordpress.step2')}</p>
                <CopyField
                  value={scenarioId}
                  testid="install-sheet-agent-id"
                  copyTestid="install-sheet-agent-id-copy"
                  onCopy={onAgentIdCopy}
                />
              </Step>
              <Step index={3}>{t('website:install.sheet.wordpress.step3')}</Step>
            </ol>
          </div>
        );
      case 'shopify':
        return (
          <ol className="space-y-4">
            <Step index={1}>
              <Trans
                t={t}
                i18nKey={`website:install.sheet.shopify.${shopifyVariant}.step1`}
                components={shopifyInstructionComponents}
              />
            </Step>
            <Step index={2}>
              <p>
                <Trans
                  t={t}
                  i18nKey={`website:install.sheet.shopify.${shopifyVariant}.step2`}
                  components={shopifyInstructionComponents}
                />
              </p>
              {!isShopifyAppUser && (
                <div data-testid="install-sheet-shopify-snippet">
                  <WidgetScript
                    script={shopifySnippet}
                    onCopy={() => onSnippetCopy(hasShopifyMcp ? 'widget_shopify' : 'widget')}
                  />
                </div>
              )}
            </Step>
            <Step index={3}>
              <Trans
                t={t}
                i18nKey={`website:install.sheet.shopify.${shopifyVariant}.step3`}
                components={shopifyInstructionComponents}
              />
            </Step>
          </ol>
        );
      case 'others':
        return (
          <ol className="space-y-4">
            <Step index={1}>{t('website:install.sheet.others.step1')}</Step>
            <Step index={2}>
              <p>{t('website:install.sheet.others.step2')}</p>
              <WidgetScript script={widgetSnippet} onCopy={() => onSnippetCopy('widget')} />
            </Step>
            <Step index={3}>{t('website:install.sheet.others.step3')}</Step>
          </ol>
        );
      case 'chat_page':
        return (
          <div className="space-y-5">
            <ShareLinkRow
              title={t('website:chat_page_link.title')}
              description={t('website:chat_page_link.description')}
              url={chatPageUrl}
              openLabel={t('website:install.sheet.open_link')}
              copyLabel={t('website:install.sheet.copy_url')}
              copiedLabel={t('common:copied')}
              testidPrefix="install-sheet-chat-page"
            />
            <ShareLinkRow
              title={t('website:public_link.widget.title')}
              description={t('website:public_link.widget.description')}
              url={widgetPreviewUrl}
              openLabel={t('website:install.sheet.open_link')}
              copyLabel={t('website:install.sheet.copy_url')}
              copiedLabel={t('common:copied')}
              testidPrefix="install-sheet-widget-preview"
            />
            <ShareLinkRow
              title={t('website:public_link.embed.title')}
              description={t('website:public_link.embed.description')}
              url={embedPreviewUrl}
              openLabel={t('website:install.sheet.open_link')}
              copyLabel={t('website:install.sheet.copy_url')}
              copiedLabel={t('common:copied')}
              testidPrefix="install-sheet-embed-preview"
            />
            <p className="text-sm text-muted-foreground">
              {t('website:install.sheet.chat_page.access_note')}
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  const titleKey =
    method === 'manual' ? 'website:install.manual.title' : `website:install.sheet.${method}.title`;
  const descriptionKey =
    method === 'manual'
      ? 'website:install.manual.description'
      : method === 'shopify'
        ? `website:install.sheet.shopify.${shopifyVariant}.description`
        : `website:install.sheet.${method}.description`;
  const docsHref =
    method === 'wordpress'
      ? DOCS_WORDPRESS_URL
      : method === 'shopify'
        ? DOCS_SHOPIFY_URL
        : DOCS_WEBSITE_URL;
  const showDocs = method !== null && method !== 'chat_page';

  return (
    <Sheet open={method !== null} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md"
        data-testid="install-method-sheet"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onOpenChange(false);
        }}
      >
        <SheetHeader className="px-0.5 flex-row items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <SheetTitle data-testid="install-method-sheet-title">
              {method && t(titleKey)}
            </SheetTitle>
            <SheetDescription>{method && t(descriptionKey)}</SheetDescription>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            aria-label={t('website:install.sheet.close')}
            data-testid="install-method-sheet-close"
            className="shrink-0"
          >
            <X className="size-4" />
          </IconButton>
        </SheetHeader>

        {showDocs && (
          <div className="px-0.5">
            <ReadDocumentationButton href={docsHref} />
          </div>
        )}

        <div className="min-w-0 flex-1 overflow-y-auto px-0.5 pt-2">{renderBody()}</div>
      </SheetContent>
    </Sheet>
  );
};
